import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Flex,
  Text,
  Heading,
  Icon,
  Select,
  Button,
  Collapse,
  IconButton,
  Badge,
  Spinner,
  useDisclosure
} from '@chakra-ui/react';
import { MapPin, Compass, Plus, ArrowRight, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
// import { AUTO_DETECT_MAPPING } from '../../constants/autoDetectMapping'; // Not used
import { mediaHasFeature as utilMediaHasFeature } from '../../utils/featureUtils';
import SiteCard from '../SiteCard';
import SearchFilters from '../SearchFilters';

// Helper function to filter media based on features, using the utility function
const getFilteredMedia = (allMedia, features, normalizeFeature) => {
  if (!Array.isArray(allMedia)) return [];
  
  // Filter for valid media first
  const validMedia = allMedia.filter(media => {
    if (!media) return false;
    if (!media.type || !media.url) return false;
    if (media.type === 'image' && !media.url.match(/^https?:\/\/.+/)) return false;
    return true;
  });
  
  // If no features to filter by, return all valid media
  if (!features?.length) return validMedia;
  
  // Filter by features using the utility function
  return validMedia.filter(media => 
    features.every(featureId => utilMediaHasFeature(media, featureId))
  );
};

export default function SitesSection({ 
  allUserSites, 
  filteredSites, 
  filters, 
  onFilterChange, 
  onFilterReset,
  sortBy,
  onSortChange,
  featuresMetadata,
  normalizeFeature,
  isLoading,
  onLoadSites,
  initialSiteCount 
}) {
  const { isOpen, onToggle } = useDisclosure();
  // Use initialSiteCount for the badge until sites are loaded, then use allUserSites.length
  const displaySiteCount = (allUserSites?.length > 0) ? allUserSites.length : (initialSiteCount || 0);

  return (
    <Box variant="dashboardSection" p={0}>
      <VStack align="stretch" spacing={0}>
        {/* Collapsible Header */}
        <Flex
          p={6}
          cursor="pointer"
          onClick={() => {
            if (!isOpen && !allUserSites?.length && onLoadSites) {
              onLoadSites(); // Trigger lazy loading when first opened
            }
            onToggle();
          }}
          _hover={{ bg: 'bg.accent' }}
          transition="background-color 0.2s"
          justify="space-between"
          align="center"
        >
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              bg="bg.accent"
              color="brand.primary"
              borderRadius="md"
              justify="center"
              align="center"
            >
              <Icon as={MapPin} weight="duotone" fontSize="xl" />
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="lg" fontFamily="heading" color="text.default">
                My Megalithic Sites
              </Heading>
              <HStack spacing={2}>
                <Badge variant="subtle" colorScheme="gray">
                  {displaySiteCount} sites
                </Badge>
                <Text fontSize="sm" color="text.muted">
                  Click to {isOpen ? 'hide' : 'view'} your submissions
                </Text>
              </HStack>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            <Button
              size="md"
              variant="journal"
              leftIcon={<Plus weight="bold" />}
              as={Link}
              to="/create-site"
              onClick={(e) => e.stopPropagation()}
            >
              New Site
            </Button>
            <IconButton
              icon={<Icon as={isOpen ? CaretUp : CaretDown} weight="bold" />}
              variant="ghost"
              size="md"
              aria-label={isOpen ? "Hide sites" : "Show sites"}
              color="text.muted"
            />
          </HStack>
        </Flex>

        {/* Collapsible Content */}
        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={6} px={6} pb={6}>
            {/* Loading State */}
            {isLoading && (
              <Flex justify="center" py={8}>
                <VStack spacing={3}>
                  <Spinner size="lg" color="brand.primary" />
                  <Text color="text.muted">Loading your sites...</Text>
                </VStack>
              </Flex>
            )}

            {/* Content - only render when not loading and have data */}
            {!isLoading && allUserSites?.length > 0 && (
              <>
                {/* Controls */}
                <HStack justify="flex-end" spacing={4}>
                  <Select
                    value={sortBy}
                    onChange={(e) => onSortChange(e.target.value)}
                    w="200px"
                    size="md"
                    variant="journal"
                    icon={<Icon as={Compass} />}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="country">Sort by Country</option>
                  </Select>
                </HStack>

                {/* Filters */}
                <Box
                  borderRadius="md"
                  bg="bg.accent"
                  p={4}
                  boxShadow="inner"
                >
                  <SearchFilters
                    filters={filters}
                    onFilterChange={onFilterChange}
                    onReset={onFilterReset}
                    variant="flat"
                    showMaterialType={false}
                    showEra={false}
                    showVerification={false}
                    showCountry={true}
                    onlyShowFeatures={true}
                  />
                </Box>

                {/* Site Cards Grid */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  {filteredSites
                    .sort((a, b) => {
                      if (sortBy === 'name') {
                        return a.name?.localeCompare(b.name || '') || 0;
                      } else if (sortBy === 'country') {
                        return (a.location?.country || '').localeCompare(b.location?.country || '') || 0;
                      }
                      return 0;
                    })
                    .map((site) => (
                      <SiteCard
                        key={site.id}
                        site={site}
                        isSubmitter={true}
                        minimal={true}
                        variant="journal"
                        showFilteredMedia={filters.features?.length > 0}
                        filteredMedia={getFilteredMedia(site.media || [], filters.features, normalizeFeature)}
                        systemFilteredMedia={getFilteredMedia(site.media || [], filters.features, normalizeFeature)}
                        onFeatureFilter={(feature) => {
                          const featureId = typeof feature === 'string' ? feature : feature.id;
                          const normalizedId = normalizeFeature(featureId);
                          
                          const exists = filters.features.some(id => normalizeFeature(id) === normalizedId);
                          
                          const newFeatures = exists
                            ? filters.features.filter(id => normalizeFeature(id) !== normalizedId)
                            : [...filters.features, normalizedId];
                            
                          onFilterChange({ features: newFeatures });
                        }}
                        filters={{...filters, featuresMetadata}}
                      />
                    ))}
                  
                  {filteredSites.length === 0 && (
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      py={8}
                      bg="bg.accent"
                      borderRadius="md"
                      gridColumn="1 / -1"
                    >
                      <Icon as={Compass} fontSize="3xl" color="text.muted" mb={3} />
                      <Text color="text.muted">No sites match the selected filters</Text>
                      <Button
                        mt={4}
                        variant="journal"
                        size="sm"
                        onClick={onFilterReset}
                      >
                        Reset Filters
                      </Button>
                    </Flex>
                  )}
                </SimpleGrid>
                
                {/* Explore All Button */}
                {filteredSites.length > 0 && (
                  <Flex justify="center" mt={4}>
                    <Button
                      as={Link}
                      to="/search"
                      size="lg"
                      variant="journal"
                      rightIcon={<Icon as={ArrowRight} weight="bold" />}
                    >
                      Explore All Sites
                    </Button>
                  </Flex>
                )}
              </>
            )}

            {/* Empty State when no sites */}
            {!isLoading && allUserSites?.length === 0 && (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                bg="bg.accent"
                borderRadius="md"
              >
                <Icon as={MapPin} fontSize="4xl" color="text.muted" mb={4} />
                <Heading size="md" color="text.default" mb={2}>
                  No sites submitted yet
                </Heading>
                <Text color="text.muted" mb={6} textAlign="center">
                  Start documenting megalithic sites and contribute to our growing database
                </Text>
                <Button
                  as={Link}
                  to="/create-site"
                  variant="journal"
                  leftIcon={<Plus weight="bold" />}
                  size="lg"
                >
                  Submit Your First Site
                </Button>
              </Flex>
            )}
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}
